import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import Paper from "@mui/material/Paper";
import { alpha, keyframes } from "@mui/material/styles";
import GitHubCalendar from "react-github-calendar";

// Icons for expertise categories
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import StorageIcon from "@mui/icons-material/Storage";
import CloudIcon from "@mui/icons-material/Cloud";
import CodeIcon from "@mui/icons-material/Code";
import SecurityIcon from "@mui/icons-material/Security";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Expertise categories data
const expertiseCategories = [
  {
    title: "Enterprise SaaS & APIs",
    icon: <AccountBalanceWalletIcon />,
    color: "#a855f7",
    skills: [
      { name: "Oracle Fusion API Integration", level: 90 },
      { name: "REST API Design", level: 95 },
      { name: "Role-Based Access Control", level: 90 },
      { name: "Invoice Workflows", level: 92 },
      { name: "Accounting Modules", level: 88 },
      { name: "ERP Data Sync", level: 86 },
    ],
  },
  {
    title: "MERN Stack",
    icon: <StorageIcon />,
    color: "#007FFF",
    skills: [
      { name: "React.js", level: 98 },
      { name: "Next.js", level: 92 },
      { name: "Node.js", level: 95 },
      { name: "MongoDB", level: 92 },
      { name: "Express.js", level: 95 },
      { name: "Dashboards & Admin Panels", level: 94 },
      { name: "Socket.io", level: 88 },
    ],
  },
  {
    title: "Web3 & Cloud",
    icon: <CloudIcon />,
    color: "#00D4AA",
    skills: [
      { name: "WalletConnect", level: 90 },
      { name: "Ethers.js", level: 95 },
      { name: "MetaMask Integration", level: 95 },
      { name: "AWS (EC2, S3, Lambda)", level: 85 },
      { name: "DigitalOcean", level: 90 },
      { name: "Firebase", level: 95 },
    ],
  },
];

// What I do cards
const services = [
  {
    icon: <CodeIcon />,
    title: "Enterprise SaaS Development",
    description:
      "Building scalable MERN and Next.js platforms with admin workflows, dashboards, APIs, and production-ready architecture.",
  },
  {
    icon: <SecurityIcon />,
    title: "Oracle Fusion API Integration",
    description:
      "Connecting SaaS and finance products with Oracle Fusion APIs for invoices, customer records, sync status, and operational reporting.",
  },
  {
    icon: <StorageIcon />,
    title: "Invoicing & Accounting Systems",
    description:
      "Designing invoice flows, GL structures, P&L reports, balance sheet views, approvals, and role-based finance tools.",
  },
  {
    icon: <AccountBalanceWalletIcon />,
    title: "Web3 & Wallet Integration",
    description:
      "Creating wallet-connected DApps with MetaMask, WalletConnect, Ethers.js, transaction states, and smart contract interactions.",
  },
];

function About() {
  return (
    <Box
      component="section"
      id="about"
      sx={{
        py: { xs: 8, md: 12 },
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Background accents */}
      <Box
        sx={{
          position: "absolute",
          top: "30%",
          left: "-5%",
          width: "25%",
          height: "25%",
          background: "radial-gradient(circle, rgba(0, 127, 255, 0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg">
        {/* About Me Section */}
        <Box sx={{ mb: 10 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  animation: `${fadeInUp} 0.6s ease-out`,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    letterSpacing: 3,
                    mb: 1,
                    display: "block",
                  }}
                >
                  About Me
                </Typography>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    background: "linear-gradient(135deg, #ffffff 0%, #007FFF 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Enterprise SaaS, MERN, Oracle Fusion API and Web3 Developer
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2, lineHeight: 1.8 }}
                >
                  I'm <strong style={{ color: "#007FFF" }}>Rahman Idrees</strong>, a passionate
                  MERN Stack and Web3 Engineer with over 5 years of experience building
                  production-ready applications. I specialize in enterprise SaaS products,
                  Oracle Fusion API integrations, invoicing systems, accounting workflows,
                  React/Next.js dashboards, and wallet-connected DApps.
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2, lineHeight: 1.8 }}
                >
                  My work spans frontend product experiences, backend API architecture,
                  financial data flows, ERP integration points, authentication, reporting,
                  and admin operations. I use safe implementation patterns for complex
                  business workflows instead of forcing one-size-fits-all templates.
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                >
                  I can support projects from early technical planning through implementation,
                  launch, maintenance, and targeted performance improvements.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {services.map((service, index) => (
                  <Grid item xs={12} sm={6} key={service.title}>
                    <Card
                      sx={{
                        height: "100%",
                        animation: `${fadeInUp} 0.6s ease-out`,
                        animationDelay: `${index * 0.1}s`,
                        animationFillMode: "both",
                      }}
                    >
                      <CardContent>
                        <Avatar
                          sx={{
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.1),
                            color: "primary.main",
                            mb: 2,
                          }}
                        >
                          {service.icon}
                        </Avatar>
                        <Typography variant="subtitle1" component="h3" fontWeight={600} sx={{ mb: 1 }}>
                          {service.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Expertise Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: "primary.main",
                fontWeight: 600,
                letterSpacing: 3,
                mb: 1,
                display: "block",
              }}
            >
              Skills
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
              }}
            >
              Technical Expertise
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto" }}
            >
              Specialized in enterprise web products, finance workflows, integrations,
              dashboards, and Web3 application interfaces.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {expertiseCategories.map((category, index) => (
              <Grid item xs={12} md={4} key={category.title}>
                <Paper
                  sx={{
                    p: 3,
                    height: "100%",
                    backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
                    backdropFilter: "blur(10px)",
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    animation: `${fadeInUp} 0.6s ease-out`,
                    animationDelay: `${index * 0.15}s`,
                    animationFillMode: "both",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: alpha(category.color, 0.5),
                      boxShadow: `0 0 30px ${alpha(category.color, 0.1)}`,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <Avatar
                      sx={{
                        backgroundColor: alpha(category.color, 0.15),
                        color: category.color,
                      }}
                    >
                      {category.icon}
                    </Avatar>
                    <Typography variant="h6" component="h3" fontWeight={600}>
                      {category.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {category.skills.map((skill) => (
                      <Box key={skill.name}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="body2">{skill.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {skill.level}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: (theme) => alpha(theme.palette.divider, 0.2),
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${skill.level}%`,
                              borderRadius: 2,
                              backgroundColor: category.color,
                              transition: "width 1s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* GitHub Activity */}
        <Box>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" component="h2" fontWeight={600} sx={{ mb: 1 }}>
              GitHub Contribution Graph
            </Typography>
            <Typography variant="body2" color="text.secondary">
              My open-source activity over the past year
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 3,
              borderRadius: 3,
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.3),
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              overflow: "auto",
            }}
          >
            <GitHubCalendar
              username="soumyajit4419"
              colorScheme="dark"
              fontSize={12}
              blockSize={12}
              blockMargin={4}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default About;
